import { useState, useEffect, useMemo } from 'react';
import { supabase, supabaseAdmin } from '../../supabaseClient';
import { paymentsService } from '../../services/payments.service';
import { packagesService }       from '../../services/packages.service';
import { organizationsService }  from '../../services/organizations.service';
import Modal     from '../ui/Modal';
import Table     from '../ui/Table';
import StatCard  from '../ui/StatCard';
import SearchBar from '../ui/SearchBar';

const ACCENT = '#00e5a0';
const TABS   = [
  { id: 'overview',  icon: '📊', label: 'Overview'      },
  { id: 'packages',  icon: '📦', label: 'Packages'       },
  { id: 'orgs',      icon: '🏢', label: 'Organizations'  },
  { id: 'billing',   icon: '💳', label: 'Billing'        },
  { id: 'settings',  icon: '⚙',  label: 'Settings'       },
];

const LIM_FIELDS = [
  { key: 'asset_limit',    label: 'Assets',              icon: '📦', monthly: false },
  { key: 'user_limit',     label: 'Users',               icon: '👥', monthly: false },
  { key: 'antenna_limit',  label: 'Antennas',            icon: '📡', monthly: false },
  { key: 'facility_limit', label: 'Facilities',          icon: '🏭', monthly: false },
  { key: 'request_limit',  label: 'Requests / month',   icon: '📋', monthly: true  },
  { key: 'delivery_limit', label: 'Deliveries / month', icon: '🚚', monthly: true  },
];

const FEAT_FIELDS = [
  // Core modules
  { key: 'can_assets',        label: '📦 Asset Management',      group: 'Core' },
  { key: 'can_checkout',      label: '🔄 Check-In / Check-Out',  group: 'Core' },
  { key: 'can_facilities',    label: '🏭 Facilities',            group: 'Core' },
  { key: 'can_users',         label: '👥 User Management',       group: 'Core' },
  // Operations
  { key: 'can_requests',      label: '📋 Transfer Requests',     group: 'Operations' },
  { key: 'can_deliveries',    label: '🚚 Delivery Management',   group: 'Operations' },
  { key: 'can_movements',     label: '📍 Movement History',      group: 'Operations' },
  // RFID & Tracking
  { key: 'can_rfid',          label: '📡 RFID Reader Config',    group: 'RFID' },
  { key: 'can_antennas',      label: '🔊 Antenna Management',    group: 'RFID' },
  { key: 'can_realtime',      label: '⚡ Real-time Tracking',    group: 'RFID' },
  { key: 'can_qr_label',      label: '🏷 QR Label Printing',     group: 'Core' },
  { key: 'can_floor_maps',    label: '🗺 Floor Map Editor',      group: 'RFID' },
  // Analytics
  { key: 'can_reports',       label: '📈 Reports & Analytics',   group: 'Analytics' },
  { key: 'can_export',        label: '💾 Export CSV / JSON',     group: 'Analytics' },
  // Advanced
  { key: 'can_api_access',    label: '🔌 API Access',            group: 'Advanced' },
  { key: 'can_custom_roles',  label: '🔐 Custom Role Permissions',group: 'Advanced' },
  { key: 'can_audit_logs',    label: '📝 Audit Logs',            group: 'Advanced' },
  { key: 'can_webhooks',      label: '🔗 Webhooks',              group: 'Advanced' },
];

const PKG_COLORS = ['#a0b4c0','#5b9cf6','#00e5a0','#b89de8','#f5a623','#e05757','#22d3ee','#f472b6'];

/* ─────────────────────────────────────────────────────────────── */

export default function SuperAdminPage({ accountManagerMode = false, currentUserId = null }) {
  const AM_TABS = ['billing'];
  const [tab, setTab] = useState(accountManagerMode ? 'billing' : 'overview');
  const [packages, setPackages] = useState([]);
  const [orgs,     setOrgs]     = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState('');
  const [loginAsModal, setLoginAsModal] = useState(null);

  // Account managers only see their assigned orgs
  const visibleOrgs = accountManagerMode
    ? orgs.filter(o => o.account_manager_id === currentUserId)
    : orgs;
  const [accountManagers, setAccountManagers] = useState([]);
  const [amModal, setAmModal]   = useState(false);  // add AM modal
  const [amForm,  setAmForm]    = useState({ email:'', name:'' });
  const [amSaving, setAmSaving] = useState(false);

  // Modals
  const [pkgModal,    setPkgModal]    = useState(null);   // null | 'new' | pkg obj
  const [pkgForm,     setPkgForm]     = useState({});
  const [orgModal,    setOrgModal]    = useState(null);   // null | 'new' | org obj
  const [sysNameEdits, setSysNameEdits] = useState({});  // { [orgId]: value }
  const [sysNameSaving, setSysNameSaving] = useState({});
  const [platformName, setPlatformName]   = useState(() => localStorage.getItem('rfid_wms_platform_name') || 'RFID WMS');
  const [bridgeDlWin,  setBridgeDlWin]   = useState('');
  const [showPlanWidget, setShowPlanWidget] = useState(true);
  const [planWidgetSaved, setPlanWidgetSaved] = useState(false);
  const [brandLogo,      setBrandLogo]      = useState('');   // base64 or URL
  const [brandFavicon,   setBrandFavicon]   = useState('');
  const [brandSaved,     setBrandSaved]     = useState(false);
  const [bridgeDlMac,  setBridgeDlMac]   = useState('');
  const [bridgeDlSaved,setBridgeDlSaved] = useState(false);
  const [platformSaved, setPlatformSaved] = useState(false);
  const saveBranding = async () => {
    try {
      const { supabase: sb } = await import('../../supabaseClient');
      const rows = [];
      if (brandLogo)    rows.push({ key: 'brand_logo',    value: brandLogo,    updated_at: new Date().toISOString() });
      if (brandFavicon) rows.push({ key: 'brand_favicon', value: brandFavicon, updated_at: new Date().toISOString() });
      if (rows.length === 0) return;
      await sb.from('platform_settings').upsert(rows, { onConflict: 'key' });
      // Apply favicon immediately
      if (brandFavicon) {
        let link = document.querySelector("link[rel*='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'shortcut icon'; document.head.appendChild(link); }
        link.href = brandFavicon;
      }
      // Store logo in localStorage for sidebar/login
      if (brandLogo) localStorage.setItem('rfid_wms_brand_logo', brandLogo);
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 2500);
      showToast('✓ Branding saved — reload the app to see changes');
    } catch(err) { showToast('❌ ' + err.message); }
  };

  const clearBranding = async (field) => {
    try {
      const { supabase: sb } = await import('../../supabaseClient');
      const key = field === 'logo' ? 'brand_logo' : 'brand_favicon';
      await sb.from('platform_settings').upsert([{ key, value: '', updated_at: new Date().toISOString() }], { onConflict: 'key' });
      if (field === 'logo')    { setBrandLogo('');    localStorage.removeItem('rfid_wms_brand_logo'); }
      if (field === 'favicon') setBrandFavicon('');
      showToast(`✓ ${field === 'logo' ? 'Logo' : 'Favicon'} removed`);
    } catch(err) { showToast('❌ ' + err.message); }
  };

  const savePlanWidget = async (val) => {
    setShowPlanWidget(val);
    try {
      const { supabase } = await import('../../supabaseClient');
      await supabase.from('platform_settings').upsert([
        { key: 'show_plan_widget', value: String(val), updated_at: new Date().toISOString() },
      ], { onConflict: 'key' });
      setPlanWidgetSaved(true);
      setTimeout(() => setPlanWidgetSaved(false), 2000);
      showToast(`✓ Plan & Usage widget ${val ? 'visible' : 'hidden'} for all users`);
    } catch(err) { showToast('❌ ' + err.message); }
  };

  const saveBridgeLinks = async () => {
    try {
      const { supabase } = await import('../../supabaseClient');
      // Upsert both keys into platform_settings
      await supabase.from('platform_settings').upsert([
        { key: 'rfid_bridge_dl_win', value: bridgeDlWin, updated_at: new Date().toISOString() },
        { key: 'rfid_bridge_dl_mac', value: bridgeDlMac, updated_at: new Date().toISOString() },
      ], { onConflict: 'key' });
      setBridgeDlSaved(true);
      setTimeout(() => setBridgeDlSaved(false), 2500);
      showToast('✓ Bridge download links saved');
    } catch(err) { showToast('❌ ' + err.message); }
  };

  const savePlatformName = () => {
    localStorage.setItem('rfid_wms_platform_name', platformName);
    setPlatformSaved(true);
    setTimeout(() => setPlatformSaved(false), 2000);
  };
  const [orgForm,     setOrgForm]     = useState({});
  const [overrideOrg, setOverrideOrg] = useState(null);  // org to edit overrides
  const [createdOrg,  setCreatedOrg]  = useState(null);  // newly created org + invite code

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2600); };

  const addAccountManager = async () => {
    if (!amForm.email || !amForm.name || !amForm.password) return;
    setAmSaving(true);
    try {
      if (!supabaseAdmin) throw new Error('REACT_APP_SUPABASE_SERVICE_KEY not set in .env');

      // Use Admin API — bypasses email confirmation, never fails with "Database error"
      const { data: userData, error: createErr } = await supabaseAdmin.auth.admin.createUser({
        email:             amForm.email,
        password:          amForm.password,
        email_confirm:     true,   // mark as confirmed immediately
        user_metadata:     { name: amForm.name, role: 'Admin', org_id: null },
      });
      if (createErr) throw new Error(createErr.message);
      const newId = userData.user?.id;
      if (!newId) throw new Error('No user ID returned');

      // Upsert profile in case trigger didn't fire
      await supabase.from('profiles').upsert(
        { id: newId, email: amForm.email, name: amForm.name, role: 'Admin', org_id: null },
        { onConflict: 'id' }
      );

      // Add to superadmins
      const { error: insErr } = await supabase
        .from('superadmins')
        .insert([{ id: newId, role: 'account_manager', name: amForm.name }]);
      if (insErr) throw new Error(insErr.message.includes('duplicate')
        ? 'This user is already a superadmin or account manager.'
        : insErr.message);

      showToast('✓ Account manager created — they can log in immediately');
      setAmModal(false);
      setAmForm({ email: '', name: '', password: '', showPass: false });
      load();
    } catch(err) { showToast('❌ ' + err.message); }
    setAmSaving(false);
  };

  const assignAccountManager = async (orgId, amId) => {
    await organizationsService.update(orgId, { account_manager_id: amId || null });
    setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, account_manager_id: amId || null } : o));
    showToast(amId ? '✓ Account manager assigned' : '✓ Account manager removed');
  };

  const removeAccountManager = async (id, name) => {
    if (!window.confirm(`Remove ${name} as account manager?`)) return;
    await supabase.from('superadmins').delete().eq('id', id);
    showToast('✓ Account manager removed');
    load();
  };

  useEffect(() => { load(); loadBridgeLinks(); }, []);

  const loadBridgeLinks = async () => {
    try {
      const { supabase } = await import('../../supabaseClient');
      const { data } = await supabase.from('platform_settings')
        .select('key, value')
        .in('key', ['rfid_bridge_dl_win', 'rfid_bridge_dl_mac', 'show_plan_widget', 'brand_logo', 'brand_favicon']);
      if (data) {
        const win  = data.find(r => r.key === 'rfid_bridge_dl_win');
        const mac  = data.find(r => r.key === 'rfid_bridge_dl_mac');
        const plan = data.find(r => r.key === 'show_plan_widget');
        if (win)  setBridgeDlWin(win.value || '');
        if (mac)  setBridgeDlMac(mac.value || '');
        if (plan) setShowPlanWidget(plan.value !== 'false');
        const logo    = data.find(r => r.key === 'brand_logo');
        const favicon = data.find(r => r.key === 'brand_favicon');
        if (logo?.value)    setBrandLogo(logo.value);
        if (favicon?.value) setBrandFavicon(favicon.value);
      }
    } catch(e) { /* table may not exist yet */ }
  };

  const load = async () => {
    setLoading(true);
    const [pkgs, orgList, platformStats, amRaw] = await Promise.all([
      packagesService.getAll(),
      organizationsService.getAll(),
      organizationsService.getPlatformStats(),
      supabase.from('superadmins').select('id, name, role').eq('role', 'account_manager'),
    ]);
    setPackages(pkgs);
    setOrgs(orgList);
    setStats(platformStats);

    // Fetch profiles separately for each AM to get their email
    const amRows = amRaw.data || [];
    console.log('[AM] raw superadmins rows:', amRaw.data, 'error:', amRaw.error);
    if (amRows.length > 0) {
      const ids = amRows.map(a => a.id);
      const { data: profileRows } = await supabase
        .from('profiles')
        .select('id, email, name')
        .in('id', ids);
      const profileMap = Object.fromEntries((profileRows || []).map(p => [p.id, p]));
      setAccountManagers(amRows.map(a => ({
        id:    a.id,
        name:  a.name || profileMap[a.id]?.name || '',
        email: profileMap[a.id]?.email || '',
      })));
    } else {
      setAccountManagers([]);
    }
    setLoading(false);
  };

  /* ── Package CRUD ─────────────────────────────────────────── */
  const openNewPkg = () => {
    setPkgForm({
      name: '', description: '', price_monthly: 0, price_yearly: 0,
      color: '#5b9cf6', is_active: true, is_popular: false,
      asset_limit: 100, user_limit: 5, antenna_limit: 5, facility_limit: 2,
      request_limit: 100, delivery_limit: 50,
      can_assets: true, can_checkout: true, can_facilities: true, can_users: true,
      can_requests: false, can_deliveries: false, can_movements: false,
      can_rfid: false, can_antennas: false, can_realtime: false, can_floor_maps: false,
      can_reports: false, can_export: false,
      can_api_access: false, can_custom_roles: false, can_audit_logs: false, can_webhooks: false,
    });
    setPkgModal('new');
  };

  const openEditPkg = (pkg) => { setPkgForm({ ...pkg }); setPkgModal(pkg); };

  const savePkg = async () => {
    if (!pkgForm.name) return;
    try {
      if (pkgModal === 'new') {
        const saved = await packagesService.create({ ...pkgForm, sort_order: packages.length + 1 });
        setPackages(prev => [...prev, saved]);
        showToast(`✓ Package "${saved.name}" created`);
      } else {
        const saved = await packagesService.update(pkgModal.id, pkgForm);
        setPackages(prev => prev.map(p => p.id === saved.id ? saved : p));
        showToast(`✓ Package "${saved.name}" updated`);
      }
      setPkgModal(null);
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  const quickTogglePkg = async (pkg, field) => {
    try {
      const updated = { ...pkg, [field]: !pkg[field] };
      const saved   = await packagesService.update(pkg.id, { [field]: !pkg[field] });
      setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, [field]: !pkg[field] } : p));
      showToast(`✓ ${pkg.name} — ${field === 'is_active' ? (!pkg[field] ? 'activated' : 'hidden') : (!pkg[field] ? 'marked popular' : 'unmarked popular')}`);
    } catch(err) { showToast(`❌ ${err.message}`); }
  };

  const deletePkg = async (id, name) => {
    if (!window.confirm(`Delete package "${name}"? This cannot be undone.`)) return;
    try {
      await packagesService.delete(id);
      setPackages(prev => prev.filter(p => p.id !== id));
      showToast(`✓ Package "${name}" deleted`);
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  /* ── Organization CRUD ────────────────────────────────────── */
  const openNewOrg = () => {
    const starterPkg = packages.find(p => p.name.toLowerCase() === 'starter') || packages[0];
    setOrgForm({ name: '', email: '', phone: '', country: 'Malaysia', packageId: starterPkg?.id || '', billing: 'monthly', account_manager_id: '' });
    setOrgModal('new');
  };

  const saveOrg = async () => {
    if (!orgForm.name) return;
    try {
      const org = await organizationsService.create({ name: orgForm.name, email: orgForm.email, phone: orgForm.phone, country: orgForm.country });
      // Always assign a package — default to Starter if none selected
      const pkgId = orgForm.packageId || packages.find(p => p.name.toLowerCase() === 'starter')?.id || packages[0]?.id;
      if (pkgId) await organizationsService.createSubscription(org.id, pkgId, orgForm.billing);
      await load();
      setCreatedOrg({ ...org, packageName: packages.find(p => p.id === pkgId)?.name || 'Starter' });
      setOrgModal(null);
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  const changePackage = async (orgId, packageId) => {
    try {
      await organizationsService.changePackage(orgId, packageId);
      await load();
      showToast('✓ Package changed');
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  const toggleOrgStatus = async (org) => {
    try {
      if (org.status === 'active') await organizationsService.suspend(org.id);
      else await organizationsService.activate(org.id);
      await load();
      showToast(`✓ Organization ${org.status === 'active' ? 'suspended' : 'activated'}`);
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  /* ── Override form ────────────────────────────────────────── */
  const [overrideForm, setOverrideForm] = useState({});

  /* ── Billing ─────────────────────────────────────────────── */
  const [billingModal,  setBillingModal]  = useState(null);
  const [billingForm,   setBillingForm]   = useState({});
  const [billingTab,    setBillingTab]    = useState('subscription'); // subscription | payments | record
  const [payments,      setPayments]      = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payForm,       setPayForm]       = useState({});
  const [paySaving,     setPaySaving]     = useState(false);
  const [overdueCount,  setOverdueCount]  = useState(0);
  const [billingSearch, setBillingSearch] = useState('');
  const [billingFilter, setBillingFilter] = useState('all');

  const billingMetrics = useMemo(() => {
    let mrr = 0, active = 0, trialing = 0, suspended = 0;
    visibleOrgs.forEach(org => {
      const sub = org.subscriptions?.[0];
      const pkg = packages.find(p => p.id === sub?.package_id);
      if (!sub || !pkg) return;
      if (sub.status === 'active') {
        mrr += sub.billing_cycle === 'yearly'
          ? (pkg.price_yearly || pkg.price_monthly * 10) / 12
          : (pkg.price_monthly || 0);
        active++;
      } else if (sub.status === 'trialing') { trialing++; }
      else if (sub.status === 'suspended')  { suspended++; }
    });
    return { mrr, arr: mrr * 12, active, trialing, suspended };
  }, [visibleOrgs, packages]);

  const filteredBillingOrgs = useMemo(() => {
    let list = visibleOrgs;
    if (billingFilter !== 'all') list = list.filter(o => o.subscriptions?.[0]?.status === billingFilter);
    if (billingSearch) {
      const q = billingSearch.toLowerCase();
      list = list.filter(o => o.name?.toLowerCase().includes(q) || o.email?.toLowerCase().includes(q));
    }
    return list;
  }, [orgs, billingFilter, billingSearch]);

  const saveSysName = async (orgId) => {
    const val = sysNameEdits[orgId] ?? orgs.find(o=>o.id===orgId)?.system_name ?? '';
    setSysNameSaving(p=>({...p,[orgId]:true}));
    await organizationsService.update(orgId, { system_name: val });
    setOrgs(prev => prev.map(o => o.id===orgId ? {...o, system_name: val} : o));
    setSysNameSaving(p=>({...p,[orgId]:false}));
  };

  const openBillingModal = async (org) => {
    const sub = org.subscriptions?.[0] || {};
    setBillingForm({
      status:        sub.status        || 'trialing',
      billing_cycle: sub.billing_cycle || 'monthly',
      trial_ends_at: sub.trial_ends_at?.slice(0, 10) || '',
      renewal_date:  sub.renewal_date  || '',
      package_id:    sub.package_id    || '',
      notes:         sub.notes         || '',
    });
    setBillingTab('subscription');
    setBillingModal(org);
    setPaymentsLoading(true);
    try {
      await paymentsService.markOverdue();
      const rows = await paymentsService.listByOrg(org.id);
      setPayments(rows);
      const oc = await paymentsService.getOverdueCount();
      setOverdueCount(oc);
    } catch(e) { console.error(e); }
    setPaymentsLoading(false);
    // Prefill record payment form
    const pkg = packages.find(p => p.id === sub.package_id);
    const amount = sub.billing_cycle === 'yearly'
      ? ((pkg?.price_yearly || (pkg?.price_monthly || 0) * 10))
      : (pkg?.price_monthly || 0);
    const invoiceNo = await paymentsService.getNextInvoiceNo();
    const today = new Date().toISOString().slice(0,10);
    const due   = new Date(); due.setDate(due.getDate()+14);
    setPayForm({
      invoice_no:     invoiceNo,
      amount:         amount.toFixed(2),
      currency:       'MYR',
      status:         'unpaid',
      payment_method: 'bank_transfer',
      due_date:       due.toISOString().slice(0,10),
      paid_date:      today,
      period_start:   today,
      period_end:     sub.renewal_date || '',
      notes:          '',
    });
  };

  const saveBilling = async () => {
    try {
      const changes = {
        status:        billingForm.status,
        billing_cycle: billingForm.billing_cycle,
        trial_ends_at: billingForm.trial_ends_at ? new Date(billingForm.trial_ends_at).toISOString() : null,
        renewal_date:  billingForm.renewal_date   || null,
        notes:         billingForm.notes,
      };
      if (billingForm.package_id !== billingModal.subscriptions?.[0]?.package_id) {
        await organizationsService.changePackage(billingModal.id, billingForm.package_id);
      }
      await organizationsService.updateSubscription(billingModal.id, changes);
      if (billingForm.status === 'suspended') await organizationsService.suspend(billingModal.id);
      else await organizationsService.activate(billingModal.id);
      await load();
      showToast('✓ Subscription updated');
      setBillingModal(null);
    } catch(err) { showToast(`❌ ${err.message}`); }
  };

  const extendTrial = async (org, days) => {
    try {
      const sub  = org.subscriptions?.[0];
      const base = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : new Date();
      if (base < new Date()) base.setTime(Date.now());
      base.setDate(base.getDate() + days);
      await organizationsService.updateSubscription(org.id, { trial_ends_at: base.toISOString(), status: 'trialing' });
      await load();
      showToast(`✓ Trial extended by ${days} days`);
    } catch(err) { showToast(`❌ ${err.message}`); }
  };

  const openOverrides = (org) => {
    const sub = org.subscriptions?.[0] || {};
    setOverrideForm({
      asset_limit_override:    sub.asset_limit_override    ?? '',
      user_limit_override:     sub.user_limit_override     ?? '',
      antenna_limit_override:  sub.antenna_limit_override  ?? '',
      facility_limit_override: sub.facility_limit_override ?? '',
      request_limit_override:  sub.request_limit_override  ?? '',
      delivery_limit_override: sub.delivery_limit_override ?? '',
    });
    setOverrideOrg(org);
  };

  const saveOverrides = async () => {
    const clean = {};
    Object.entries(overrideForm).forEach(([k, v]) => {
      clean[k] = v === '' ? null : parseInt(v);
    });
    try {
      await organizationsService.setOverrides(overrideOrg.id, clean);
      await load();
      showToast('✓ Overrides saved');
      setOverrideOrg(null);
    } catch (err) { showToast(`❌ ${err.message}`); }
  };

  /* ── Render ───────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#7090a0', fontSize: 13 }}>
      Loading superadmin data…
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 20, color: '#e8edf2' }}>
            {accountManagerMode ? '💼 Account Manager' : '🛡 SuperAdmin Console'}
          </div>
          <div style={{ fontSize: 12, color: '#7090a0', marginTop: 3 }}>
            {accountManagerMode ? 'View platform overview and manage your organization billing' : 'Manage packages, organizations, and platform-wide settings'}
          </div>
        </div>
        <div style={{
          background: accountManagerMode ? '#f5a62318' : '#e0575718',
          border: `1px solid ${accountManagerMode ? '#f5a62330' : '#e0575730'}`,
          borderRadius: 8, padding: '6px 14px', fontSize: 11,
          color: accountManagerMode ? '#f5a623' : '#e05757', fontWeight: 700,
        }}>
          {accountManagerMode ? '🟡 ACCOUNT MANAGER' : '🔴 SUPERADMIN MODE'}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #1a2a3a', paddingBottom: 0 }}>
        {TABS.filter(t => !accountManagerMode || AM_TABS.includes(t.id)).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="tab-btn"
            style={{ color: tab === t.id ? ACCENT : '#a0b4c0', borderBottomColor: tab === t.id ? ACCENT : 'transparent' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            <StatCard icon="🏢" label="Total Orgs"    value={accountManagerMode ? visibleOrgs.length : (stats?.totalOrgs || 0)}   sub={accountManagerMode ? `${visibleOrgs.filter(o=>o.status==='active').length} active` : `${stats?.activeOrgs || 0} active`} accent="#5b9cf6" />
            <StatCard icon="📦" label="Total Assets"  value={stats?.totalAssets || 0} sub="across all orgs"                    accent="#00e5a0" />
            <StatCard icon="👥" label="Total Users"   value={stats?.totalUsers || 0}  sub="across all orgs"                    accent="#b89de8" />
            <StatCard icon="📋" label="Packages"      value={packages.filter(p => p.is_active).length} sub="active packages"   accent="#f5a623" />
          </div>

          {/* Orgs by package */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {packages.filter(p => p.is_active).map(pkg => {
              const count = visibleOrgs.filter(o => o.subscriptions?.[0]?.package_id === pkg.id).length;
              return (
                <div key={pkg.id} className="card" style={{ padding: '16px 20px', borderColor: pkg.color + '40' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: pkg.color }} />
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#e8edf2' }}>{pkg.name}</div>
                    <div style={{ marginLeft: 'auto', fontSize: 11, color: '#7090a0' }}>MYR {pkg.price_monthly}/mo</div>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: pkg.color, fontFamily: "'Syne',sans-serif" }}>{count}</div>
                  <div style={{ fontSize: 11, color: '#7090a0' }}>organization{count !== 1 ? 's' : ''} on this plan</div>
                </div>
              );
            })}
          </div>

          {/* Recent orgs */}
          <div className="card">
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 14, color: '#e8edf2', marginBottom: 14 }}>
              🏢 Recent Organizations
            </div>
            <Table
              cols={[
                { key: 'name',   label: 'Organization' },
                { key: 'status', label: 'Status', render: v => <StatusDot status={v} /> },
                { key: 'subscriptions', label: 'Package', render: v => {
                  const pkg = packages.find(p => p.id === v?.[0]?.package_id);
                  return pkg ? <span style={{ color: pkg.color, fontWeight: 700, fontSize: 12 }}>{pkg.name}</span> : '—';
                }},
                { key: 'email',  label: 'Contact' },
                { key: 'created_at', label: 'Joined', render: v => v?.slice(0, 10) },
              ]}
              rows={orgs.slice(0, 8)}
              onRowClick={org => { setOrgForm(org); setOrgModal(org); }}
            />
          </div>
        </div>
      )}

      {/* ── PACKAGES ── */}
      {tab === 'packages' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#7090a0' }}>{packages.length} packages configured</div>
            <button className="btn" style={{ background: ACCENT, color: '#060b10' }} onClick={openNewPkg}>
              + New Package
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                orgCount={orgs.filter(o => o.subscriptions?.[0]?.package_id === pkg.id).length}
                onEdit={() => openEditPkg(pkg)}
                onDelete={() => deletePkg(pkg.id, pkg.name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── ORGANIZATIONS ── */}
      {tab === 'orgs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#7090a0' }}>{orgs.length} organizations</div>
            <button className="btn" style={{ background: ACCENT, color: '#060b10' }} onClick={openNewOrg}>
              + New Organization
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orgs.map(org => (
              <OrgRow
                key={org.id}
                org={org}
                packages={packages}
                accountManagers={accountManagers}
                onChangePackage={changePackage}
                onToggleStatus={() => toggleOrgStatus(org)}
                onOverrides={() => openOverrides(org)}
                onAssignAM={(amId) => assignAccountManager(org.id, amId)}
              />
            ))}
            {orgs.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '50px 0', color: '#7090a0' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🏢</div>
                No organizations yet. Create one to get started.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BILLING TAB ── */}
      {tab === 'billing' && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Revenue metrics */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:14 }}>
            {[
              { label:'MRR',         value:`MYR ${billingMetrics.mrr.toFixed(0)}`,  sub:'monthly recurring',     icon:'💰', c:'#00e5a0' },
              { label:'ARR',         value:`MYR ${billingMetrics.arr.toFixed(0)}`,  sub:'annual recurring',      icon:'📈', c:'#5b9cf6' },
              { label:'Active',      value:billingMetrics.active,                    sub:'paying subscriptions',  icon:'✅', c:'#00e5a0' },
              { label:'Trialing',    value:billingMetrics.trialing,                  sub:'on free trial',         icon:'⏳', c:'#f5a623' },
              { label:'Suspended',   value:billingMetrics.suspended,                 sub:'suspended accounts',    icon:'⏸', c:'#e05757' },
            ].map(s => (
              <div key={s.label} style={{ background:'#0d1520', border:`1px solid ${s.c}20`, borderRadius:12, padding:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:s.c+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>{s.icon}</div>
                  <div style={{ fontSize:10, color:'#7090a0', fontWeight:600, textTransform:'uppercase' }}>{s.label}</div>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color:s.c, fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
                <div style={{ fontSize:11, color:'#7090a0', marginTop:4 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Revenue by plan */}
          <div style={{ background:'#0d1520', border:'1px solid #1a2a3a', borderRadius:12, padding:20 }}>
            <div style={{ fontSize:11, color:'#7090a0', textTransform:'uppercase', letterSpacing:'1.2px', fontWeight:600, marginBottom:16 }}>Revenue by Package</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {packages.filter(p => p.is_active).map(pkg => {
                const pkgOrgs  = visibleOrgs.filter(o => o.subscriptions?.[0]?.package_id === pkg.id && o.subscriptions?.[0]?.status === 'active');
                const pkgMrr   = pkgOrgs.reduce((sum, o) => {
                  const sub = o.subscriptions?.[0];
                  return sum + (sub?.billing_cycle === 'yearly' ? (pkg.price_yearly || pkg.price_monthly * 10) / 12 : pkg.price_monthly);
                }, 0);
                const pct = billingMetrics.mrr > 0 ? (pkgMrr / billingMetrics.mrr) * 100 : 0;
                return (
                  <div key={pkg.id} style={{ display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:pkg.color, flexShrink:0 }} />
                    <div style={{ width:90, fontSize:12, fontWeight:600, color:'#c8d6e0' }}>{pkg.name}</div>
                    <div style={{ flex:1, height:8, background:'#1a2a3a', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:pkg.color, borderRadius:4, transition:'width .4s' }} />
                    </div>
                    <div style={{ width:80, fontSize:12, color:'#a0b4c0', textAlign:'right', fontFamily:"'IBM Plex Mono',monospace" }}>MYR {pkgMrr.toFixed(0)}</div>
                    <div style={{ width:40, fontSize:11, color:'#7090a0', textAlign:'right' }}>{pkgOrgs.length} org{pkgOrgs.length !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
              {billingMetrics.mrr === 0 && <div style={{ textAlign:'center', padding:'20px 0', color:'#7090a0', fontSize:12 }}>No active subscriptions yet</div>}
            </div>
          </div>

          {/* Filter + search */}
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ display:'flex', gap:2 }}>
              {[['all','All'],['active','Active'],['trialing','Trialing'],['suspended','Suspended']].map(([id,label]) => (
                <button key={id} className="tab-btn" onClick={() => setBillingFilter(id)}
                  style={{ color:billingFilter===id?ACCENT:'#a0b4c0', borderBottomColor:billingFilter===id?ACCENT:'transparent' }}>
                  {label}
                  <span style={{ marginLeft:5, background:'#1a2a3a', borderRadius:8, padding:'1px 6px', fontSize:10, color:'#a0b4c0' }}>
                    {id==='all' ? visibleOrgs.length : visibleOrgs.filter(o=>o.subscriptions?.[0]?.status===id).length}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ position:'relative', marginLeft:'auto' }}>
              <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#7090a0' }}>🔍</span>
              <input className="inp" style={{ paddingLeft:30, width:220 }} placeholder="Search org or email…"
                value={billingSearch} onChange={e => setBillingSearch(e.target.value)} />
            </div>
          </div>

          {/* Subscriptions table */}
          <div style={{ background:'#0d1520', border:'1px solid #1a2a3a', borderRadius:12, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #1a2a3a' }}>
                  {['Organization','Package','Billing Cycle','Status','Renewal','Payment','MRR','Account Manager','Actions'].map(h => (
                    <th key={h} className="th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredBillingOrgs.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign:'center', padding:'40px 0', color:'#7090a0' }}>No organizations found</td></tr>
                )}
                {filteredBillingOrgs.map((org, i) => {
                  const sub   = org.subscriptions?.[0];
                  const pkg   = packages.find(p => p.id === sub?.package_id);
                  const mrr   = sub?.status === 'active' ? (sub?.billing_cycle === 'yearly' ? (pkg?.price_yearly || (pkg?.price_monthly||0)*10)/12 : (pkg?.price_monthly||0)) : 0;
                  const subStatus = sub?.status || 'none';
                  const statusColors = { active:'#00e5a0', trialing:'#f5a623', suspended:'#e05757', none:'#a0b4c0' };
                  const trialDaysLeft = sub?.trial_ends_at ? Math.ceil((new Date(sub.trial_ends_at) - Date.now()) / 86400000) : null;
                  return (
                    <tr key={org.id} style={{ borderBottom: i < filteredBillingOrgs.length-1 ? '1px solid #0d1520':'none' }}>
                      <td className="td">
                        <div style={{ fontWeight:700, color:'#e8edf2', fontSize:13 }}>{org.name}</div>
                        <div style={{ fontSize:11, color:'#7090a0' }}>{org.email || '—'}</div>
                      </td>
                      <td className="td">
                        {pkg ? <span style={{ color:pkg.color, fontWeight:700, fontSize:12 }}>{pkg.name}</span> : <span style={{ color:'#7090a0' }}>—</span>}
                      </td>
                      <td className="td">
                        <span style={{ fontSize:11, color:'#c8d6e0', fontFamily:"'IBM Plex Mono',monospace" }}>{sub?.billing_cycle || '—'}</span>
                      </td>
                      <td className="td">
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600,
                          background:(statusColors[subStatus])+'18', color:statusColors[subStatus], border:`1px solid ${statusColors[subStatus]}30` }}>
                          {subStatus === 'active' ? '✅' : subStatus === 'trialing' ? '⏳' : subStatus === 'suspended' ? '⏸' : '○'} {subStatus}
                        </span>
                      </td>
                      <td className="td">
                        {(() => {
                          const rd = sub?.renewal_date;
                          if (!rd) return <span style={{ color:'#7090a0' }}>—</span>;
                          const daysLeft = Math.ceil((new Date(rd) - Date.now()) / 86400000);
                          const isOverdue = daysLeft < 0;
                          const isWarning = daysLeft >= 0 && daysLeft <= 7;
                          return (
                            <div>
                              <div style={{ fontSize:11, fontFamily:"'IBM Plex Mono',monospace",
                                color: isOverdue ? '#e05757' : isWarning ? '#f5a623' : '#c8d6e0' }}>
                                {rd}
                              </div>
                              {isOverdue && <div style={{ fontSize:10, color:'#e05757', fontWeight:700 }}>OVERDUE</div>}
                              {isWarning && <div style={{ fontSize:10, color:'#f5a623' }}>{daysLeft}d left</div>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="td">
                        {(() => {
                          const ps = sub?.payment_status || 'unpaid';
                          const c  = ps === 'paid' ? '#00e5a0' : ps === 'overdue' ? '#e05757' : '#f5a623';
                          return (
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
                              background: c+'18', border:`1px solid ${c}30`, color:c }}>
                              {ps === 'paid' ? '✓ Paid' : ps === 'overdue' ? '⚠ Overdue' : '⏳ Unpaid'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="td">
                        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color: mrr > 0 ? '#00e5a0' : '#7090a0' }}>
                          {mrr > 0 ? `MYR ${mrr.toFixed(0)}` : '—'}
                        </span>
                      </td>
                      <td className="td">
                        {(() => {
                          const am = accountManagers.find(a => a.id === org.account_manager_id);
                          return am ? (
                            <span style={{ fontSize:11, fontWeight:600, color:'#f5a623' }}>💼 {am.name}</span>
                          ) : (
                            <select className="sel" style={{ fontSize:11, padding:'3px 6px', color:'#7090a0' }}
                              value={org.account_manager_id || ''}
                              onChange={e => assignAccountManager(org.id, e.target.value)}>
                              <option value="">— Assign —</option>
                              {accountManagers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="td" onClick={e => e.stopPropagation()}>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          {sub?.status === 'trialing' && (
                            <button className="btn" style={{ fontSize:10, padding:'4px 8px', background:'#f5a62318', border:'1px solid #f5a62330', color:'#f5a623' }}
                              onClick={() => extendTrial(org, 14)} title="Extend trial by 14 days">+14d</button>
                          )}
                          <button className="btn" style={{ fontSize:10, padding:'4px 10px', background:'#1a2a3a', color:'#c8d6e0' }}
                            onClick={() => openBillingModal(org)}>✏ Manage</button>
                          <button className="btn" style={{ fontSize:10, padding:'4px 10px', background:'#5b9cf618', border:'1px solid #5b9cf630', color:'#5b9cf6', fontWeight:700 }}
                            onClick={() => setLoginAsModal(org)}>🔑 Login</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SETTINGS TAB ── */}
      {tab === 'settings' && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <div className="card" style={{ padding:24 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:'#e8edf2', marginBottom:4 }}>System Names</div>
            <div style={{ fontSize:12, color:'#7090a0', marginBottom:24 }}>
              Set a custom system name per organization. This appears on the login page, sidebar, and browser tab.
            </div>
            {/* ─── Account Managers ─────────────────────────────────── */}
          <div className="card" style={{ padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
              <div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:'#e8edf2' }}>Account Managers</div>
                <div style={{ fontSize:12, color:'#7090a0', marginTop:3 }}>Sales staff who can view Overview & Billing and log in to client organizations.</div>
              </div>
              <button className="btn" style={{ background:ACCENT, color:'#060b10', fontSize:12, padding:'8px 16px', flexShrink:0 }}
                onClick={() => { setAmForm({ email:'', name:'' }); setAmModal(true); }}>
                + Add Account Manager
              </button>
            </div>

            {accountManagers.length === 0 ? (
              <div style={{ textAlign:'center', padding:'28px 0', color:'#7090a0', fontSize:13 }}>
                No account managers yet. Add one above.
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', marginTop:18 }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #1a2a3a' }}>
                    {['Name','Email','Role','Orgs Assigned',''].map(h => (
                      <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:'#7090a0', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accountManagers.map((am, i) => {
                    const assigned = orgs.filter(o => o.account_manager_id === am.id);
                    return (
                      <tr key={am.id} style={{ borderBottom: i < accountManagers.length-1 ? '1px solid #0d1520' : 'none' }}>
                        <td style={{ padding:'12px', fontSize:13, fontWeight:700, color:'#e8edf2' }}>{am.name}</td>
                        <td style={{ padding:'12px', fontSize:12, color:'#a0b4c0', fontFamily:"'IBM Plex Mono',monospace" }}>{am.email}</td>
                        <td style={{ padding:'12px' }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:5, background:'rgba(245,166,35,.12)', border:'1px solid rgba(245,166,35,.25)', color:'#f5a623' }}>
                            💼 Account Manager
                          </span>
                        </td>
                        <td style={{ padding:'12px' }}>
                          {assigned.length > 0 ? (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                              {assigned.slice(0,3).map(o => (
                                <span key={o.id} style={{ fontSize:10, padding:'1px 6px', borderRadius:4, background:'#1a2a3a', color:'#a0b4c0' }}>{o.name}</span>
                              ))}
                              {assigned.length > 3 && <span style={{ fontSize:10, color:'#7090a0' }}>+{assigned.length-3} more</span>}
                            </div>
                          ) : <span style={{ fontSize:11, color:'#7090a0' }}>—</span>}
                        </td>
                        <td style={{ padding:'12px' }}>
                          <button className="btn"
                            style={{ fontSize:11, padding:'5px 12px', background:'rgba(224,87,87,.12)', border:'1px solid rgba(224,87,87,.25)', color:'#e05757' }}
                            onClick={() => removeAccountManager(am.id, am.name)}>
                            🗑 Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Platform name */}
            <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'16px 20px', marginBottom:24 }}>
              <div style={{ fontSize:11, color:'#7090a0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:4 }}>Platform Name</div>
              <div style={{ fontSize:11, color:'#7090a0', marginBottom:10 }}>Shown in the SuperAdmin sidebar. Does not affect tenant-facing pages.</div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input
                  className="inp"
                  style={{ flex:1, maxWidth:320, fontSize:14, fontWeight:700 }}
                  value={platformName}
                  onChange={e => setPlatformName(e.target.value)}
                  onKeyDown={e => e.key==='Enter' && savePlatformName()}
                  placeholder="e.g. RFID WMS"
                />
                <button className="btn"
                  style={{ background: platformSaved?'#00e5a0':'#00e5a0', color:'#060b10', padding:'8px 20px', fontSize:13 }}
                  onClick={savePlatformName}>
                  {platformSaved ? '✓ Saved' : 'Save'}
                </button>
              </div>
            </div>
            <div style={{ height:1, background:'#1a2a3a', marginBottom:20 }}/>

            {/* Bridge Download Links */}
            <div style={{ fontSize:13, fontWeight:700, color:'#c8d6e0', marginBottom:16 }}>RFID Bridge Download Links</div>
            <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'16px 20px', marginBottom:24 }}>
              <div style={{ fontSize:11, color:'#7090a0', marginBottom:14 }}>
                These links appear as download buttons on the Settings → RFID Devices page for all users.
                Upload the bridge zip files to Supabase Storage or any CDN and paste the public URLs here.
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div>
                  <div style={{ fontSize:11, color:'#7090a0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>🖥 Windows Download URL</div>
                  <input className="inp" style={{ width:'100%', fontFamily:"'IBM Plex Mono',monospace", fontSize:12 }}
                    value={bridgeDlWin}
                    onChange={e => setBridgeDlWin(e.target.value)}
                    placeholder="https://your-bucket.supabase.co/storage/v1/object/public/rfid-bridge/rfid-bridge-windows.zip"
                  />
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#7090a0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>🍎 Mac Download URL</div>
                  <input className="inp" style={{ width:'100%', fontFamily:"'IBM Plex Mono',monospace", fontSize:12 }}
                    value={bridgeDlMac}
                    onChange={e => setBridgeDlMac(e.target.value)}
                    placeholder="https://your-bucket.supabase.co/storage/v1/object/public/rfid-bridge/rfid-bridge-mac.zip"
                  />
                </div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <button className="btn" style={{ background:ACCENT, color:'#060b10', padding:'8px 20px', fontSize:13 }}
                    onClick={saveBridgeLinks}>
                    {bridgeDlSaved ? '✓ Saved' : 'Save Links'}
                  </button>
                  {bridgeDlWin && (
                    <a href={bridgeDlWin} target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:'#5b9cf6', textDecoration:'none' }}>
                      Test Windows link ↗
                    </a>
                  )}
                  {bridgeDlMac && (
                    <a href={bridgeDlMac} target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:'#5b9cf6', textDecoration:'none' }}>
                      Test Mac link ↗
                    </a>
                  )}
                </div>
                <div style={{ background:'#1a2a3a', borderRadius:8, padding:'10px 14px', fontSize:11, color:'#7090a0' }}>
                  💡 <strong style={{ color:'#c8d6e0' }}>How to host:</strong> Supabase → Storage → New bucket <code style={{ color:ACCENT }}>rfid-bridge</code> (public) → Upload zips → Copy URL
                </div>
              </div>
            </div>

            <div style={{ height:1, background:'#1a2a3a', marginBottom:20 }}/>

            {/* Branding */}
            <div style={{ fontSize:13, fontWeight:700, color:'#c8d6e0', marginBottom:16 }}>🎨 Branding</div>
            <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'18px 20px', marginBottom:24 }}>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

                {/* Logo */}
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#c8d6e0', marginBottom:4 }}>App Logo</div>
                  <div style={{ fontSize:11, color:'#7090a0', marginBottom:10 }}>
                    Replaces system name in sidebar & login page.<br/>
                    <span style={{ color:'#a0b4c0' }}>Recommended: PNG, 200×60px, transparent bg. Upload to Supabase Storage then paste the public URL.</span>
                  </div>
                  {brandLogo && (
                    <div style={{ marginBottom:10 }}>
                      <img src={brandLogo} alt="Logo preview"
                        style={{ height:48, maxWidth:'100%', objectFit:'contain', background:'#1a2a3a', borderRadius:8, padding:'8px 12px' }}
                        onError={e => e.target.style.display='none'}/>
                    </div>
                  )}
                  <div style={{ display:'flex', gap:8 }}>
                    <input className="inp" style={{ flex:1, fontSize:12 }}
                      value={brandLogo} placeholder="https://your-bucket.supabase.co/storage/v1/object/public/branding/logo.png"
                      onChange={e => setBrandLogo(e.target.value)}/>
                    {brandLogo && (
                      <button className="btn" style={{ background:'#e0575718', border:'1px solid #e0575730', color:'#e05757', fontSize:12, padding:'7px 12px' }}
                        onClick={() => clearBranding('logo')}>✕</button>
                    )}
                  </div>
                </div>

                {/* Favicon */}
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#c8d6e0', marginBottom:4 }}>Favicon</div>
                  <div style={{ fontSize:11, color:'#7090a0', marginBottom:10 }}>
                    Browser tab icon.<br/>
                    <span style={{ color:'#a0b4c0' }}>Recommended: PNG, 32×32px. Upload to Supabase Storage and paste URL.</span>
                  </div>
                  {brandFavicon && (
                    <div style={{ marginBottom:10, display:'flex', alignItems:'center', gap:10 }}>
                      <img src={brandFavicon} alt="Favicon preview"
                        style={{ width:32, height:32, objectFit:'contain', background:'#1a2a3a', borderRadius:6, padding:4 }}
                        onError={e => e.target.style.display='none'}/>
                      <span style={{ fontSize:11, color:'#7090a0' }}>preview</span>
                    </div>
                  )}
                  <div style={{ display:'flex', gap:8 }}>
                    <input className="inp" style={{ flex:1, fontSize:12 }}
                      value={brandFavicon} placeholder="https://your-bucket.supabase.co/storage/v1/object/public/branding/favicon.png"
                      onChange={e => setBrandFavicon(e.target.value)}/>
                    {brandFavicon && (
                      <button className="btn" style={{ background:'#e0575718', border:'1px solid #e0575730', color:'#e05757', fontSize:12, padding:'7px 12px' }}
                        onClick={() => clearBranding('favicon')}>✕</button>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginTop:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                {brandSaved && <span style={{ fontSize:12, color:'#00e5a0' }}>✓ Saved — reload app to apply</span>}
                {!brandSaved && <span/>}
                <button className="btn" style={{ background:'#00e5a0', color:'#060b10', fontWeight:700, fontSize:12 }}
                  onClick={saveBranding} disabled={brandSaved}>
                  💾 Save Branding
                </button>
              </div>
              <div style={{ marginTop:20, paddingTop:16, borderTop:'1px solid #1a2a3a' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#c8d6e0', marginBottom:4 }}>System Name</div>
                <div style={{ fontSize:11, color:'#7090a0', marginBottom:12 }}>
                  Shown in sidebar and login page when no logo is uploaded. Set per organisation.
                </div>
                {orgs.map((org, i) => {
                  const editVal = sysNameEdits[org.id] ?? org.system_name ?? '';
                  const saving  = sysNameSaving[org.id];
                  return (
                    <div key={org.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                      <div style={{ width:140, flexShrink:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#c8d6e0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{org.name}</div>
                        <div style={{ fontSize:10, color:'#7090a0' }}>{org.status}</div>
                      </div>
                      <input className="inp" style={{ flex:1, fontSize:12 }}
                        value={editVal}
                        placeholder={org.name + ' WMS'}
                        onChange={e => setSysNameEdits(p=>({...p,[org.id]:e.target.value}))}
                        onKeyDown={e=>{ if(e.key==='Enter') saveSysName(org.id); }}
                      />
                      <button className="btn"
                        style={{ background:'#00e5a0', color:'#060b10', fontSize:12, padding:'7px 14px', flexShrink:0, opacity:saving?0.6:1 }}
                        onClick={()=>saveSysName(org.id)} disabled={saving}>
                        {saving ? '…' : 'Save'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dashboard Widgets */}
            <div style={{ fontSize:13, fontWeight:700, color:'#c8d6e0', marginBottom:16 }}>Dashboard Widgets</div>
            <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'16px 20px', marginBottom:24 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#c8d6e0', marginBottom:3 }}>💳 Plan & Usage card</div>
                  <div style={{ fontSize:11, color:'#7090a0' }}>Shows subscription plan, usage bars and pricing on user dashboard</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  {planWidgetSaved && <span style={{ fontSize:11, color:'#00e5a0' }}>✓ Saved</span>}
                  <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}
                    onClick={() => savePlanWidget(!showPlanWidget)}>
                    <div style={{
                      width:40, height:22, borderRadius:11,
                      background: showPlanWidget ? '#00e5a0' : '#1a2a3a',
                      position:'relative', transition:'background .2s', flexShrink:0,
                    }}>
                      <div style={{
                        position:'absolute', top:3, left: showPlanWidget ? 20 : 3,
                        width:16, height:16, borderRadius:'50%',
                        background:'#fff', transition:'left .2s',
                      }}/>
                    </div>
                    <span style={{ fontSize:12, color: showPlanWidget ? '#00e5a0' : '#a0b4c0', fontWeight:600 }}>
                      {showPlanWidget ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      )}

      {/* ── PACKAGE MODAL ── */}
      {pkgModal && (
        <Modal title={pkgModal === 'new' ? 'New Package' : `Edit — ${pkgForm.name}`} onClose={() => setPkgModal(null)} width={620}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Name + color + price */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
              <div>
                <Lbl>Package Name *</Lbl>
                <input className="inp" value={pkgForm.name || ''} placeholder="e.g. Pro"
                  onChange={e => setPkgForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Lbl>Colour</Lbl>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {PKG_COLORS.map(c => (
                    <div key={c} onClick={() => setPkgForm(f => ({ ...f, color: c }))}
                      style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer',
                        border: pkgForm.color === c ? '3px solid #fff' : '2px solid transparent',
                        transform: pkgForm.color === c ? 'scale(1.15)' : 'none', transition: 'all .15s' }} />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Lbl>Description</Lbl>
              <input className="inp" value={pkgForm.description || ''} placeholder="Short description shown on pricing page…"
                onChange={e => setPkgForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <Lbl>Monthly Price (MYR)</Lbl>
                <input className="inp" type="number" min="0" value={pkgForm.price_monthly ?? ''}
                  onChange={e => setPkgForm(f => ({ ...f, price_monthly: +e.target.value }))} />
              </div>
              <div>
                <Lbl>Yearly Price (MYR)</Lbl>
                <input className="inp" type="number" min="0" value={pkgForm.price_yearly ?? ''}
                  onChange={e => setPkgForm(f => ({ ...f, price_yearly: +e.target.value }))} />
              </div>
            </div>

            {/* Limits */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                📊 Resource Limits
                <span style={{ fontSize: 10, color: '#7090a0', fontWeight: 400 }}>— Enter -1 for unlimited</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {LIM_FIELDS.map(f => (
                  <div key={f.key}>
                    <Lbl>{f.icon} {f.label}</Lbl>
                    <input className="inp" type="number" min="-1" value={pkgForm[f.key] ?? ''}
                      onChange={e => setPkgForm(prev => ({ ...prev, [f.key]: +e.target.value }))} />
                  </div>
                ))}
              </div>
            </div>

            {/* Feature flags */}
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0' }}>🚀 Feature Access</div>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn" style={{ fontSize:10, padding:'3px 10px', background:'#00e5a018', border:'1px solid #00e5a030', color:'#00e5a0' }}
                    onClick={() => setPkgForm(prev => { const u={...prev}; FEAT_FIELDS.forEach(f=>u[f.key]=true); return u; })}>All On</button>
                  <button className="btn" style={{ fontSize:10, padding:'3px 10px', background:'#1a2a3a', color:'#a0b4c0' }}
                    onClick={() => setPkgForm(prev => { const u={...prev}; FEAT_FIELDS.forEach(f=>u[f.key]=false); return u; })}>All Off</button>
                </div>
              </div>
              {['Core','Operations','RFID','Analytics','Advanced'].map(group => (
                <div key={group} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <div style={{ fontSize:10, color:'#7090a0', textTransform:'uppercase', letterSpacing:'1px', fontWeight:600 }}>{group}</div>
                    <div style={{ flex:1, height:'1px', background:'#1a2a3a' }}/>
                    <button className="btn" style={{ fontSize:9, padding:'2px 8px', background:'#1a2a3a', color:'#a0b4c0' }}
                      onClick={() => { const all=FEAT_FIELDS.filter(f=>f.group===group).every(f=>pkgForm[f.key]); setPkgForm(prev=>{const u={...prev};FEAT_FIELDS.filter(f=>f.group===group).forEach(f=>u[f.key]=!all);return u;}); }}>
                      {FEAT_FIELDS.filter(f=>f.group===group).every(f=>pkgForm[f.key]) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {FEAT_FIELDS.filter(f => f.group === group).map(f => (
                      <Toggle key={f.key} label={f.label} value={!!pkgForm[f.key]}
                        onChange={v => setPkgForm(prev => ({ ...prev, [f.key]: v }))} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Flags */}
            <div style={{ display: 'flex', gap: 20 }}>
              <Toggle label="Active (visible to new signups)" value={!!pkgForm.is_active}
                onChange={v => setPkgForm(f => ({ ...f, is_active: v }))} />
              <Toggle label="Most Popular badge" value={!!pkgForm.is_popular}
                onChange={v => setPkgForm(f => ({ ...f, is_popular: v }))} />
            </div>

            {/* Preview */}
            <div style={{ background: '#060b10', border: `1px solid ${pkgForm.color || '#1a2a3a'}40`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: pkgForm.color || '#a0b4c0', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: pkgForm.color || '#e8edf2' }}>{pkgForm.name || 'Package Name'}</div>
                <div style={{ fontSize: 11, color: '#7090a0', marginTop: 2 }}>MYR {pkgForm.price_monthly || 0}/mo · {pkgForm.asset_limit === -1 ? '∞' : pkgForm.asset_limit} assets · {pkgForm.user_limit === -1 ? '∞' : pkgForm.user_limit} users</div>
              </div>
              {pkgForm.is_popular && <div style={{ marginLeft: 'auto', background: '#f5a62318', border: '1px solid #f5a62330', borderRadius: 6, padding: '3px 10px', fontSize: 10, color: '#f5a623', fontWeight: 700 }}>⭐ Most Popular</div>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn" style={{ flex: 1, background: 'none', border: '1px solid #1a2a3a', color: '#a0b4c0' }} onClick={() => setPkgModal(null)}>Cancel</button>
            <button className="btn" style={{ flex: 2, background: ACCENT, color: '#060b10' }} onClick={savePkg}>
              {pkgModal === 'new' ? 'Create Package' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── NEW ORG MODAL ── */}
      {orgModal === 'new' && (
        <Modal title="New Organization" onClose={() => setOrgModal(null)} width={500}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <Lbl>Company Name *</Lbl>
              <input className="inp" value={orgForm.name || ''} placeholder="e.g. Acme Sdn. Bhd."
                onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <Lbl>Billing Email</Lbl>
                <input className="inp" type="email" value={orgForm.email || ''}
                  onChange={e => setOrgForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Lbl>Phone</Lbl>
                <input className="inp" value={orgForm.phone || ''}
                  onChange={e => setOrgForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <Lbl>Package *</Lbl>
              <select className="sel" style={{ width: '100%' }} value={orgForm.packageId || ''}
                onChange={e => setOrgForm(f => ({ ...f, packageId: e.target.value }))}>
                <option value="">Select a package…</option>
                {packages.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name} — MYR {p.price_monthly}/mo</option>
                ))}
              </select>
            </div>
            <div>
              <Lbl>Billing Cycle</Lbl>
              <div style={{ display: 'flex', gap: 8 }}>
                {['monthly', 'yearly'].map(c => (
                  <button key={c} onClick={() => setOrgForm(f => ({ ...f, billing: c }))}
                    className="btn"
                    style={{ flex: 1, background: orgForm.billing === c ? ACCENT + '18' : 'none', border: `1px solid ${orgForm.billing === c ? ACCENT : '#1a2a3a'}`, color: orgForm.billing === c ? ACCENT : '#a0b4c0' }}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {accountManagers.length > 0 && (
              <div>
                <Lbl>Account Manager <span style={{ color:'#7090a0', fontWeight:400 }}>(optional)</span></Lbl>
                <select className="sel" style={{ width:'100%' }} value={orgForm.account_manager_id || ''}
                  onChange={e => setOrgForm(f => ({ ...f, account_manager_id: e.target.value }))}>
                  <option value="">— Unassigned —</option>
                  {accountManagers.map(am => (
                    <option key={am.id} value={am.id}>{am.name} ({am.email})</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ background: '#00e5a010', border: '1px solid #00e5a020', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#7090a0' }}>
              ✓ Organization will start with a 14-day free trial
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn" style={{ flex: 1, background: 'none', border: '1px solid #1a2a3a', color: '#a0b4c0' }} onClick={() => setOrgModal(null)}>Cancel</button>
            <button className="btn" style={{ flex: 2, background: ACCENT, color: '#060b10' }} onClick={saveOrg}
              disabled={!orgForm.name || !orgForm.packageId}>Create Organization</button>
          </div>
        </Modal>
      )}

      {/* ── OVERRIDES MODAL ── */}
      {overrideOrg && (
        <Modal title={`Custom Limits — ${overrideOrg.name}`} onClose={() => setOverrideOrg(null)} width={500}>
          <div style={{ fontSize: 12, color: '#7090a0', marginBottom: 16 }}>
            Override limits for this org only. Leave blank to use the package default.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {LIM_FIELDS.map(f => {
              const pkgId  = overrideOrg.subscriptions?.[0]?.package_id;
              const pkg    = packages.find(p => p.id === pkgId);
              const defVal = pkg?.[f.key];
              return (
                <div key={f.key}>
                  <Lbl>{f.icon} {f.label}</Lbl>
                  <input className="inp" type="number" min="-1"
                    placeholder={`Package default: ${defVal === -1 ? '∞' : defVal}`}
                    value={overrideForm[`${f.key}_override`] ?? ''}
                    onChange={e => setOverrideForm(prev => ({ ...prev, [`${f.key}_override`]: e.target.value }))} />
                </div>
              );
            })}
          </div>
          <div style={{ background: '#f5a62310', border: '1px solid #f5a62330', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#f5a623', marginTop: 16 }}>
            ⚠ Overrides take effect immediately. Use -1 for unlimited, blank to revert to package default.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn" style={{ flex: 1, background: 'none', border: '1px solid #1a2a3a', color: '#a0b4c0' }} onClick={() => setOverrideOrg(null)}>Cancel</button>
            <button className="btn" style={{ flex: 2, background: ACCENT, color: '#060b10' }} onClick={saveOverrides}>Save Overrides</button>
          </div>
        </Modal>
      )}


      {/* ── INVITE CODE REVEAL MODAL ── */}
      {createdOrg && (
        <Modal title="Organization Created!" onClose={() => setCreatedOrg(null)} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>🎉</div>
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: '#e8edf2', marginBottom: 4 }}>{createdOrg.name}</div>
              <div style={{ fontSize: 12, color: '#a0b4c0' }}>has been created on the <span style={{ color:'#5b9cf6', fontWeight:700 }}>{createdOrg.packageName}</span> plan with a 14-day free trial</div>
            </div>

            {/* Invite code box */}
            <div style={{ background: '#060b10', border: '2px solid #00e5a040', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, color: '#7090a0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                🔑 Admin Invite Code
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 28, fontWeight: 700, color: '#00e5a0', letterSpacing: '4px', marginBottom: 12 }}>
                {createdOrg.invite_code}
              </div>
              <button
                onClick={() => { navigator.clipboard?.writeText(createdOrg.invite_code); showToast('✓ Copied to clipboard'); }}
                style={{ padding: '7px 18px', borderRadius: 7, border: '1px solid #00e5a030', background: '#00e5a010', color: '#00e5a0', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                📋 Copy Code
              </button>
            </div>

            {/* Instructions */}
            <div style={{ background: '#1a2a3a', borderRadius: 10, padding: '14px 16px', textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0', marginBottom: 8 }}>Share this with the org admin:</div>
              <div style={{ fontSize: 12, color: '#a0b4c0', lineHeight: 1.7 }}>
                1. Go to the RFID WMS login page<br/>
                2. Click <strong style={{ color: '#c8d6e0' }}>"Register with Invite Code"</strong><br/>
                3. Enter the code above<br/>
                4. Fill in their details and create their account<br/>
                5. They'll be set as <strong style={{ color: '#00e5a0' }}>Admin</strong> of {createdOrg.name}
              </div>
            </div>

            <div style={{ background: '#f5a62310', border: '1px solid #f5a62330', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#f5a623' }}>
              ⚠ This invite code can only be used once. Save it before closing.
            </div>

            <button onClick={() => setCreatedOrg(null)}
              style={{ padding: '11px 0', borderRadius: 8, border: 'none', background: ACCENT, color: '#060b10', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </Modal>
      )}

      {/* ── ADD ACCOUNT MANAGER MODAL ── */}
      {amModal && (
        <Modal title="Create Account Manager" onClose={() => { setAmModal(false); setAmForm({ email:'', name:'', password:'', showPass:false }); }} width={460}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* Role info banner */}
            <div style={{ background:'#f5a62310', border:'1px solid #f5a62330', borderRadius:10, padding:'12px 16px', display:'flex', gap:10, alignItems:'flex-start' }}>
              <div style={{ fontSize:20, flexShrink:0 }}>💼</div>
              <div style={{ fontSize:12, color:'#f5a623', lineHeight:1.6 }}>
                Account Managers can log in to the platform and see <strong>Overview</strong> and <strong>Billing</strong> only. No tenant data access unless they use Login As.
              </div>
            </div>

            {/* Form */}
            <div>
              <Lbl>Full Name *</Lbl>
              <input className="inp" value={amForm.name || ''} placeholder="e.g. Ahmad Faiz"
                onChange={e => setAmForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Lbl>Email Address *</Lbl>
              <input className="inp" type="email" value={amForm.email || ''} placeholder="ahmad@yourcompany.com"
                onChange={e => setAmForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div style={{ position:'relative' }}>
              <Lbl>Password *</Lbl>
              <input
                className="inp"
                type={amForm.showPass ? 'text' : 'password'}
                value={amForm.password || ''}
                placeholder="Minimum 6 characters"
                onChange={e => setAmForm(f => ({ ...f, password: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addAccountManager()}
                style={{ paddingRight: 44 }}
              />
              <button
                onClick={() => setAmForm(f => ({ ...f, showPass: !f.showPass }))}
                style={{ position:'absolute', right:12, top:26, background:'none', border:'none', color:'#7090a0', cursor:'pointer', fontSize:16, padding:0 }}>
                {amForm.showPass ? '🙈' : '👁'}
              </button>
            </div>

            {/* Info box */}
            <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:8, padding:'10px 14px', fontSize:11, color:'#7090a0', lineHeight:1.6 }}>
              ✓ A new account will be created — no organization required.<br/>
              ✓ They can log in immediately with the email and password above.<br/>
              ✓ Password can be reset from Supabase Auth dashboard if needed.
            </div>

            <div style={{ display:'flex', gap:10, marginTop:4 }}>
              <button className="btn" style={{ flex:1, background:'none', border:'1px solid #1a2a3a', color:'#a0b4c0' }}
                onClick={() => { setAmModal(false); setAmForm({ email:'', name:'', password:'', showPass:false }); }}>
                Cancel
              </button>
              <button className="btn" style={{ flex:2, background:ACCENT, color:'#060b10' }}
                onClick={addAccountManager}
                disabled={amSaving || !amForm.email || !amForm.name || !amForm.password || (amForm.password||'').length < 6}>
                {amSaving ? 'Creating…' : '+ Create Account Manager'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── BILLING MANAGEMENT MODAL ── */}
      {/* ── LOGIN AS ORG MODAL ── */}
      {loginAsModal && (
        <LoginAsModal
          org={loginAsModal}
          packages={packages}
          onClose={() => setLoginAsModal(null)}
          showToast={showToast}
        />
      )}

      {billingModal && (
        <BillingModal
          org={billingModal}
          packages={packages}
          billingForm={billingForm}
          setBillingForm={setBillingForm}
          billingTab={billingTab}
          setBillingTab={setBillingTab}
          payments={payments}
          setPayments={setPayments}
          paymentsLoading={paymentsLoading}
          payForm={payForm}
          setPayForm={setPayForm}
          paySaving={paySaving}
          setPaySaving={setPaySaving}
          onSave={saveBilling}
          onClose={() => setBillingModal(null)}
          showToast={showToast}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
          background: toast.startsWith('❌') ? '#e05757' : ACCENT,
          color: '#060b10', padding: '12px 20px', borderRadius: 10,
          fontWeight: 700, fontSize: 13, animation: 'fadein 0.2s ease',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */

function PackageCard({ pkg, orgCount, onEdit, onDelete, onToggleActive, onTogglePopular }) {
  return (
    <div className="card" style={{ borderColor: pkg.color + '40', position: 'relative' }}>
      {pkg.is_popular && (
        <div style={{ position: 'absolute', top: -10, right: 16, background: '#f5a623', color: '#060b10', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 10 }}>
          ⭐ Most Popular
        </div>
      )}
      {!pkg.is_active && (
        <div style={{ position: 'absolute', top: 12, right: 12, background: '#e0575718', border: '1px solid #e0575730', borderRadius: 6, padding: '2px 8px', fontSize: 10, color: '#e05757', fontWeight: 700 }}>
          HIDDEN
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: pkg.color }} />
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 16, color: '#e8edf2' }}>{pkg.name}</div>
        <div style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 800, color: pkg.color, fontFamily: "'Syne',sans-serif" }}>
          MYR {pkg.price_monthly}<span style={{ fontSize: 11, fontWeight: 400, color: '#7090a0' }}>/mo</span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#a0b4c0', marginBottom: 14 }}>{pkg.description}</div>

      {/* Limits grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
        {LIM_FIELDS.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#c8d6e0' }}>
            <span style={{ fontSize: 13 }}>{f.icon}</span>
            <span style={{ color: '#7090a0' }}>{f.label.split('/')[0].trim()}:</span>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: pkg.color, fontWeight: 700 }}>
              {pkg[f.key] === -1 ? '∞' : pkg[f.key]}
            </span>
          </div>
        ))}
      </div>

      {/* Feature flags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
        {FEAT_FIELDS.filter(f => pkg[f.key]).map(f => (
          <span key={f.key} style={{ fontSize: 10, background: pkg.color + '18', border: `1px solid ${pkg.color}30`, color: pkg.color, borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
            {f.label}
          </span>
        ))}
        {FEAT_FIELDS.every(f => !pkg[f.key]) && (
          <span style={{ fontSize: 10, color: '#7090a0' }}>No feature addons</span>
        )}
      </div>

      <div style={{ borderTop: '1px solid #1a2a3a', paddingTop: 12, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: '#7090a0' }}>{orgCount} org{orgCount !== 1 ? 's' : ''} on this plan</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" style={{ fontSize: 11, padding: '4px 10px', background: '#1a2a3a', color: '#c8d6e0' }} onClick={onEdit}>✏ Edit</button>
            <button className="btn" style={{ fontSize: 11, padding: '4px 10px', background: '#e0575718', border: '1px solid #e0575730', color: '#e05757' }} onClick={onDelete}>🗑</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={onToggleActive}
            style={{ flex: 1, fontSize: 11, padding: '5px 0',
              background: pkg.is_active ? '#00e5a018' : '#e0575718',
              border: `1px solid ${pkg.is_active ? '#00e5a030' : '#e0575730'}`,
              color: pkg.is_active ? '#00e5a0' : '#e05757' }}>
            {pkg.is_active ? '● Active' : '○ Hidden'}
          </button>
          <button className="btn" onClick={onTogglePopular}
            style={{ flex: 1, fontSize: 11, padding: '5px 0',
              background: pkg.is_popular ? '#f5a62318' : '#1a2a3a',
              border: `1px solid ${pkg.is_popular ? '#f5a62330' : '#2a3a4a'}`,
              color: pkg.is_popular ? '#f5a623' : '#a0b4c0' }}>
            {pkg.is_popular ? '⭐ Popular' : '☆ Popular'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgRow({ org, packages, accountManagers = [], onChangePackage, onToggleStatus, onOverrides, onAssignAM }) {
  const sub  = org.subscriptions?.[0];
  const pkg  = packages.find(p => p.id === sub?.package_id);
  const [changing,    setChanging]    = useState(false);


  return (
    <div className="card" style={{ padding: '14px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {/* Org name + status */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: org.status === 'active' ? '#00e5a0' : '#e05757', flexShrink: 0 }} />
            <div style={{ fontWeight: 700, fontSize: 14, color: '#e8edf2' }}>{org.name}</div>
            {sub?.status === 'trialing' && (
              <span style={{ fontSize: 10, background: '#f5a62318', border: '1px solid #f5a62330', color: '#f5a623', borderRadius: 6, padding: '1px 7px', fontWeight: 700 }}>TRIAL</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#7090a0', marginTop: 2 }}>{org.email || 'No email'} · {org.country}</div>
          {org.account_manager_id && accountManagers.length > 0 && (() => {
            const am = accountManagers.find(a => a.id === org.account_manager_id);
            return am ? (
              <div style={{ display:'inline-flex', alignItems:'center', gap:5, marginTop:5,
                background:'rgba(245,166,35,.1)', border:'1px solid rgba(245,166,35,.25)',
                borderRadius:6, padding:'2px 8px', fontSize:10, color:'#f5a623', fontWeight:600 }}>
                💼 {am.name}
              </div>
            ) : null;
          })()}
        </div>

        {/* Package selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#7090a0' }}>Package:</span>
          {changing ? (
            <select className="sel" style={{ fontSize: 12 }}
              value={sub?.package_id || ''}
              onChange={e => { onChangePackage(org.id, e.target.value); setChanging(false); }}>
              {packages.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontWeight: 700, fontSize: 12, color: pkg?.color || '#a0b4c0', cursor: 'pointer' }}
              onClick={() => setChanging(true)} title="Click to change">
              {pkg?.name || 'None'} ▾
            </span>
          )}
        </div>

        {/* Joined */}
        <div style={{ fontSize: 11, color: '#7090a0', minWidth: 80 }}>
          Joined {org.created_at?.slice(0, 10)}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexWrap:'wrap' }}>
          {accountManagers.length > 0 && (
            <select
              className="sel"
              style={{ fontSize:11, padding:'4px 8px', color: org.account_manager_id ? '#f5a623' : '#a0b4c0' }}
              value={org.account_manager_id || ''}
              onChange={e => onAssignAM(e.target.value)}
              title="Assign account manager">
              <option value="">💼 Assign AM</option>
              {accountManagers.map(am => (
                <option key={am.id} value={am.id}>{am.name}</option>
              ))}
            </select>
          )}
          <button className="btn" style={{ fontSize: 11, padding: '5px 11px', background: '#1a2a3a', color: '#c8d6e0' }}
            onClick={onOverrides} title="Custom limit overrides">
            🎚 Limits
          </button>
          <button className="btn"
            style={{ fontSize: 11, padding: '5px 11px',
              background: org.status === 'active' ? '#e0575718' : '#00e5a018',
              border: `1px solid ${org.status === 'active' ? '#e0575730' : '#00e5a030'}`,
              color: org.status === 'active' ? '#e05757' : '#00e5a0' }}
            onClick={onToggleStatus}>
            {org.status === 'active' ? '⏸ Suspend' : '▶ Activate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const c = status === 'active' ? '#00e5a0' : '#e05757';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: c, fontWeight: 700 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />{status}
    </span>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => onChange(!value)}>
      <div style={{
        width: 36, height: 20, borderRadius: 10,
        background: value ? '#00e5a0' : '#1a2a3a',
        position: 'relative', transition: 'background .2s', flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute', top: 3, left: value ? 18 : 3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#fff', transition: 'left .2s',
        }} />
      </div>
      <span style={{ fontSize: 12, color: value ? '#c8d6e0' : '#a0b4c0' }}>{label}</span>
    </div>
  );
}

function Lbl({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>{children}</div>;
}

/* ── Login As Org Modal ──────────────────────────────────────────────────── */
function LoginAsModal({ org, packages, onClose, showToast }) {
  const [orgUsers,  setOrgUsers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [logging,   setLogging]   = useState(false);
  const [password,  setPassword]  = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [selected,  setSelected]  = useState(null);

  useEffect(() => {
    // Fetch all users belonging to this org
    supabase
      .from('profiles')
      .select('id, name, email, role')
      .eq('org_id', org.id)
      .order('role')
      .then(({ data }) => {
        setOrgUsers(data || []);
        const admin = (data || []).find(u => u.role === 'Admin') || data?.[0];
        if (admin) setSelected(admin);
        setLoading(false);
      });
  }, [org.id]);

  const loginAs = async () => {
    if (!selected || !password) return;
    setLogging(true);
    try {
      // Sign in as the selected org user directly
      const { error } = await supabase.auth.signInWithPassword({
        email:    selected.email,
        password: password,
      });
      if (error) throw new Error(error.message);
      // Store impersonation context so a banner shows inside the app
      localStorage.setItem('rfid_wms_impersonate_org', JSON.stringify({
        id:   org.id,
        name: org.name,
      }));
      onClose();
      // Reload to trigger auth state change → goes into TenantApp as that user
      window.location.reload();
    } catch(err) {
      showToast('❌ ' + err.message);
    }
    setLogging(false);
  };

  const sub = org.subscriptions?.[0];
  const pkg = packages.find(p => p.id === sub?.package_id);

  return (
    <Modal title={`Login as — ${org.name}`} onClose={onClose} width={460}>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {/* Org info */}
        <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'14px 18px', display:'flex', gap:14, alignItems:'center' }}>
          <div style={{ width:40, height:40, borderRadius:10, background:'#5b9cf618', border:'1px solid #5b9cf630', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🏢</div>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:'#e8edf2' }}>{org.name}</div>
            <div style={{ fontSize:11, color:'#7090a0', marginTop:2 }}>
              {pkg ? <span style={{ color: pkg.color, fontWeight:600 }}>{pkg.name}</span> : '—'}
              {' · '}
              <span style={{ color: sub?.status === 'active' ? '#00e5a0' : '#f5a623' }}>{sub?.status || 'no subscription'}</span>
            </div>
          </div>
        </div>

        {/* User selector */}
        <div>
          <Lbl>Login as user</Lbl>
          {loading ? (
            <div style={{ fontSize:12, color:'#7090a0', padding:'10px 0' }}>Loading users…</div>
          ) : orgUsers.length === 0 ? (
            <div style={{ fontSize:12, color:'#e05757', padding:'10px 0' }}>⚠ No users found in this organization yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {orgUsers.map(u => (
                <div key={u.id}
                  onClick={() => setSelected(u)}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:8, cursor:'pointer',
                    background: selected?.id === u.id ? '#5b9cf618' : '#060b10',
                    border: `1px solid ${selected?.id === u.id ? '#5b9cf640' : '#1a2a3a'}` }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'#1a2a3a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#a0b4c0', flexShrink:0 }}>
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#e8edf2' }}>{u.name}</div>
                    <div style={{ fontSize:11, color:'#7090a0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:5,
                    background: u.role === 'Admin' ? 'rgba(224,87,87,.12)' : 'rgba(91,156,246,.1)',
                    color: u.role === 'Admin' ? '#e05757' : '#5b9cf6' }}>{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Password */}
        {selected && (
          <div style={{ position:'relative' }}>
            <Lbl>Password for {selected.email}</Lbl>
            <input
              className="inp"
              type={showPass ? 'text' : 'password'}
              value={password}
              placeholder="Enter their password…"
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loginAs()}
              style={{ paddingRight:44 }}
              autoFocus
            />
            <button onClick={() => setShowPass(s => !s)}
              style={{ position:'absolute', right:12, top:26, background:'none', border:'none', color:'#7090a0', cursor:'pointer', fontSize:16 }}>
              {showPass ? '🙈' : '👁'}
            </button>
          </div>
        )}

        <div style={{ background:'#f5a62310', border:'1px solid #f5a62330', borderRadius:8, padding:'10px 14px', fontSize:11, color:'#f5a623' }}>
          ⚠ You will be fully logged in as this user. To return, sign out and log back in with your own account.
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button className="btn" style={{ flex:1, background:'none', border:'1px solid #1a2a3a', color:'#a0b4c0' }} onClick={onClose}>Cancel</button>
          <button className="btn" style={{ flex:2, background:'#5b9cf6', color:'#fff', fontWeight:700 }}
            onClick={loginAs}
            disabled={logging || !selected || !password}>
            {logging ? 'Signing in…' : `🔑 Login as ${selected?.name || '…'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   BillingModal — Tabbed: Subscription | Payment History | Record Payment
   ═══════════════════════════════════════════════════════════════════ */
function BillingModal({ org, packages, billingForm, setBillingForm, billingTab, setBillingTab,
  payments, setPayments, paymentsLoading, payForm, setPayForm, paySaving, setPaySaving,
  onSave, onClose, showToast }) {

  const sub = org.subscriptions?.[0] || {};
  const pkg = packages.find(p => p.id === (billingForm.package_id || sub.package_id));

  const TABS = [
    { id:'subscription', label:'📋 Subscription' },
    { id:'payments',     label:`💳 History ${payments.length > 0 ? `(${payments.length})` : ''}` },
    { id:'record',       label:'+ Record Payment' },
  ];

  const savePayment = async () => {
    if (!payForm.amount || !payForm.invoice_no) return;
    setPaySaving(true);
    try {
      const record = {
        org_id:         org.id,
        invoice_no:     payForm.invoice_no,
        amount:         parseFloat(payForm.amount),
        currency:       payForm.currency || 'MYR',
        status:         payForm.status,
        payment_method: payForm.payment_method,
        due_date:       payForm.due_date   || null,
        paid_date:      payForm.status === 'paid' ? (payForm.paid_date || new Date().toISOString().slice(0,10)) : null,
        period_start:   payForm.period_start || null,
        period_end:     payForm.period_end   || null,
        notes:          payForm.notes || '',
      };
      const saved = await paymentsService.create(record);
      setPayments(prev => [saved, ...prev]);

      // Update subscription payment_status
      await supabase.from('subscriptions')
        .update({ payment_status: payForm.status, updated_at: new Date().toISOString() })
        .eq('org_id', org.id);

      showToast('✓ Payment recorded');
      setBillingTab('payments');
    } catch(e) { showToast('❌ ' + e.message); }
    setPaySaving(false);
  };

  const updatePaymentStatus = async (id, status) => {
    try {
      await paymentsService.update(id, {
        status,
        paid_date: status === 'paid' ? new Date().toISOString().slice(0,10) : null,
      });
      setPayments(prev => prev.map(p => p.id === id ? { ...p, status, paid_date: status === 'paid' ? new Date().toISOString().slice(0,10) : p.paid_date } : p));
      showToast('✓ Payment updated');
    } catch(e) { showToast('❌ ' + e.message); }
  };

  const deletePayment = async (id) => {
    try {
      await paymentsService.delete(id);
      setPayments(prev => prev.filter(p => p.id !== id));
      showToast('✓ Deleted');
    } catch(e) { showToast('❌ ' + e.message); }
  };

  const generateInvoicePDF = (payment) => {
    const platformName = localStorage.getItem('rfid_wms_platform_name') || 'RFID WMS';
    const invoiceHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${payment.invoice_no}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; background:#fff; color:#1a1a2e; padding:48px; max-width:750px; margin:0 auto; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:40px; padding-bottom:24px; border-bottom:2px solid #00e5a0; }
    .brand { font-size:22px; font-weight:900; color:#060b10; letter-spacing:-0.5px; }
    .brand span { color:#00e5a0; }
    .invoice-meta { text-align:right; }
    .invoice-no { font-size:18px; font-weight:700; color:#060b10; }
    .badge { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; margin-top:6px; }
    .badge-paid { background:#00e5a018; border:1px solid #00e5a040; color:#00e5a0; }
    .badge-unpaid { background:#f5a62318; border:1px solid #f5a62340; color:#f5a623; }
    .badge-overdue { background:#e0575718; border:1px solid #e0575740; color:#e05757; }
    .section { margin-bottom:28px; }
    .section-title { font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:#a0b4c0; margin-bottom:10px; font-weight:600; }
    .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    .field label { font-size:10px; color:#a0b4c0; display:block; margin-bottom:3px; text-transform:uppercase; letter-spacing:1px; }
    .field value, .field p { font-size:13px; color:#1a1a2e; font-weight:600; }
    .amount-box { background:#060b10; border-radius:12px; padding:24px 28px; color:#fff; display:flex; justify-content:space-between; align-items:center; margin:28px 0; }
    .amount-label { font-size:12px; color:#a0b4c0; text-transform:uppercase; letter-spacing:1px; }
    .amount-value { font-size:32px; font-weight:900; color:#00e5a0; font-family:monospace; }
    table { width:100%; border-collapse:collapse; }
    th { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#a0b4c0; padding:8px 0; border-bottom:1px solid #e8ecf0; text-align:left; font-weight:600; }
    td { padding:12px 0; border-bottom:1px solid #f0f2f5; font-size:13px; }
    .footer { margin-top:40px; padding-top:20px; border-top:1px solid #e8ecf0; font-size:11px; color:#a0b4c0; text-align:center; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">${platformName.split(' ').map((w,i) => i===0 ? `<span>${w}</span>` : w).join(' ')}</div>
      <div style="font-size:12px; color:#a0b4c0; margin-top:4px;">Warehouse Management System</div>
    </div>
    <div class="invoice-meta">
      <div class="invoice-no">${payment.invoice_no}</div>
      <div style="font-size:11px; color:#a0b4c0; margin-top:4px;">Date: ${payment.created_at?.slice(0,10) || new Date().toISOString().slice(0,10)}</div>
      <div class="badge badge-${payment.status}">${payment.status.toUpperCase()}</div>
    </div>
  </div>

  <div class="grid2 section">
    <div>
      <div class="section-title">Bill To</div>
      <div class="field"><p>${org.name}</p></div>
      <div class="field" style="margin-top:4px"><p style="font-weight:400;color:#a0b4c0">${org.email || ''}</p></div>
      ${org.country ? `<div class="field" style="margin-top:4px"><p style="font-weight:400;color:#a0b4c0">${org.country}</p></div>` : ''}
    </div>
    <div>
      <div class="section-title">Subscription</div>
      <div class="field"><label>Package</label><p>${pkg?.name || '—'}</p></div>
      <div class="field" style="margin-top:8px"><label>Billing Cycle</label><p>${sub.billing_cycle || '—'}</p></div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Period</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${pkg?.name || 'Subscription'} — ${sub.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} Plan</td>
        <td style="color:#a0b4c0; font-size:12px">${payment.period_start || '—'} → ${payment.period_end || '—'}</td>
        <td style="text-align:right; font-weight:700">${payment.currency} ${parseFloat(payment.amount).toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  <div class="amount-box">
    <div>
      <div class="amount-label">Total Due</div>
      ${payment.due_date ? `<div style="font-size:11px; color:#a0b4c0; margin-top:4px;">Due: ${payment.due_date}</div>` : ''}
    </div>
    <div>
      <div class="amount-value">${payment.currency} ${parseFloat(payment.amount).toFixed(2)}</div>
    </div>
  </div>

  ${payment.payment_method ? `<div class="section"><div class="section-title">Payment Method</div><div class="field"><p>${payment.payment_method.replace('_',' ').replace(/\b\w/g, c => c.toUpperCase())}</p></div></div>` : ''}
  ${payment.notes ? `<div class="section"><div class="section-title">Notes</div><div style="font-size:12px; color:#a0b4c0;">${payment.notes}</div></div>` : ''}

  <div class="footer">
    Generated by ${platformName} · ${new Date().toLocaleDateString('en-MY', { year:'numeric', month:'long', day:'numeric' })}
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(invoiceHtml);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 500);
  };

  const statusColor = { paid:'#00e5a0', unpaid:'#f5a623', overdue:'#e05757' };

  return (
    <Modal title={`${org.name} — Billing`} onClose={onClose} width={600}>
      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#060b10', borderRadius:8, padding:4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setBillingTab(t.id)}
            className="btn" style={{ flex:1, fontSize:11, padding:'6px 8px',
              background: billingTab === t.id ? '#1a2a3a' : 'none',
              border: billingTab === t.id ? '1px solid #2a3a4a' : '1px solid transparent',
              color: billingTab === t.id ? '#e8edf2' : '#a0b4c0' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── SUBSCRIPTION TAB ── */}
      {billingTab === 'subscription' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'14px 18px' }}>
            <div style={{ fontSize:10, color:'#7090a0', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>Current Subscription</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {[
                ['Organization', org.name],
                ['Email', org.email || '—'],
                ['Package', pkg?.name || '—'],
                ['Joined', org.created_at?.slice(0,10) || '—'],
              ].map(([k,v]) => (
                <div key={k}>
                  <div style={{ fontSize:10, color:'#7090a0', marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:12, color:'#c8d6e0', fontWeight:600 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Lbl>Package</Lbl>
            <select className="sel" style={{ width:'100%' }} value={billingForm.package_id || ''}
              onChange={e => setBillingForm(f => ({ ...f, package_id: e.target.value }))}>
              <option value="">— Select package —</option>
              {packages.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.id}>{p.name} — MYR {p.price_monthly}/mo</option>
              ))}
            </select>
          </div>

          <div>
            <Lbl>Subscription Status</Lbl>
            <div style={{ display:'flex', gap:8 }}>
              {[['trialing','⏳ Trialing','#f5a623'],['active','✅ Active','#00e5a0'],['suspended','⏸ Suspended','#e05757'],['cancelled','✕ Cancelled','#a0b4c0']].map(([val,label,c]) => (
                <button key={val} onClick={() => setBillingForm(f => ({ ...f, status: val }))}
                  className="btn" style={{ flex:1, fontSize:11,
                    background: billingForm.status === val ? c+'22' : 'none',
                    border: `1px solid ${billingForm.status === val ? c : '#1a2a3a'}`,
                    color: billingForm.status === val ? c : '#a0b4c0' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <Lbl>Billing Cycle</Lbl>
              <div style={{ display:'flex', gap:8 }}>
                {[['monthly','Monthly'],['yearly','Yearly']].map(([val,label]) => (
                  <button key={val} onClick={() => setBillingForm(f => ({ ...f, billing_cycle: val }))}
                    className="btn" style={{ flex:1,
                      background: billingForm.billing_cycle === val ? ACCENT+'18' : 'none',
                      border: `1px solid ${billingForm.billing_cycle === val ? ACCENT : '#1a2a3a'}`,
                      color: billingForm.billing_cycle === val ? ACCENT : '#a0b4c0' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Lbl>Renewal Date</Lbl>
              <input className="inp" type="date" value={billingForm.renewal_date || ''}
                onChange={e => setBillingForm(f => ({ ...f, renewal_date: e.target.value }))} />
            </div>
          </div>

          {billingForm.status === 'trialing' && (
            <div>
              <Lbl>Trial End Date</Lbl>
              <input className="inp" type="date" value={billingForm.trial_ends_at || ''}
                onChange={e => setBillingForm(f => ({ ...f, trial_ends_at: e.target.value }))} />
              <div style={{ display:'flex', gap:6, marginTop:8 }}>
                {[7, 14, 30].map(d => (
                  <button key={d} className="btn" style={{ flex:1, fontSize:11, background:'#1a2a3a', color:'#c8d6e0' }}
                    onClick={() => {
                      const dt = new Date(); dt.setDate(dt.getDate() + d);
                      setBillingForm(f => ({ ...f, trial_ends_at: dt.toISOString().slice(0,10) }));
                    }}>+{d} days</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Lbl>Admin Notes (internal)</Lbl>
            <textarea className="inp" style={{ resize:'vertical', minHeight:60, fontSize:12 }}
              placeholder="e.g. Pending PO, waiting payment, special arrangement…"
              value={billingForm.notes || ''}
              onChange={e => setBillingForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {billingForm.status === 'suspended' && (
            <div style={{ background:'#e0575710', border:'1px solid #e0575730', borderRadius:8, padding:'10px 14px', fontSize:11, color:'#e05757' }}>
              ⚠ Suspending will block the organization from accessing the system.
            </div>
          )}

          <div style={{ display:'flex', gap:10, marginTop:4 }}>
            <button className="btn" style={{ flex:1, background:'none', border:'1px solid #1a2a3a', color:'#a0b4c0' }}
              onClick={onClose}>Cancel</button>
            <button className="btn" style={{ flex:2, background:ACCENT, color:'#060b10' }}
              onClick={onSave}>Save Subscription</button>
          </div>
        </div>
      )}

      {/* ── PAYMENT HISTORY TAB ── */}
      {billingTab === 'payments' && (
        <div>
          {paymentsLoading ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#7090a0' }}>Loading…</div>
          ) : payments.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>💳</div>
              <div style={{ color:'#7090a0', fontSize:13 }}>No payment records yet.</div>
              <button className="btn" style={{ marginTop:16, background:ACCENT, color:'#060b10', fontWeight:700 }}
                onClick={() => setBillingTab('record')}>+ Record First Payment</button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {payments.map(p => {
                const c = statusColor[p.status] || '#a0b4c0';
                return (
                  <div key={p.id} style={{ background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10, padding:'12px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:120 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#e8edf2', fontFamily:"'IBM Plex Mono',monospace" }}>{p.invoice_no}</div>
                        <div style={{ fontSize:10, color:'#7090a0', marginTop:2 }}>{p.created_at?.slice(0,10)}</div>
                      </div>
                      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:14, fontWeight:800, color:ACCENT }}>
                        {p.currency} {parseFloat(p.amount).toFixed(2)}
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:20,
                        background:c+'18', border:`1px solid ${c}30`, color:c }}>
                        {p.status === 'paid' ? '✓ Paid' : p.status === 'overdue' ? '⚠ Overdue' : '⏳ Unpaid'}
                      </span>
                      <div style={{ display:'flex', gap:5 }}>
                        {p.status !== 'paid' && (
                          <button className="btn" style={{ fontSize:10, padding:'3px 8px', background:'#00e5a018', border:'1px solid #00e5a030', color:'#00e5a0' }}
                            onClick={() => updatePaymentStatus(p.id, 'paid')}>✓ Mark Paid</button>
                        )}
                        {p.status === 'unpaid' && (
                          <button className="btn" style={{ fontSize:10, padding:'3px 8px', background:'#e0575718', border:'1px solid #e0575730', color:'#e05757' }}
                            onClick={() => updatePaymentStatus(p.id, 'overdue')}>⚠ Overdue</button>
                        )}
                        <button className="btn" style={{ fontSize:10, padding:'3px 8px', background:'#1a2a3a', color:'#c8d6e0' }}
                          onClick={() => generateInvoicePDF(p)} title="Download invoice PDF">📄 PDF</button>
                        <button className="btn" style={{ fontSize:10, padding:'3px 8px', background:'#e0575710', border:'1px solid #e0575720', color:'#e05757' }}
                          onClick={() => deletePayment(p.id)}>🗑</button>
                      </div>
                    </div>
                    {(p.period_start || p.period_end || p.payment_method || p.notes) && (
                      <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #1a2a3a', display:'flex', gap:16, flexWrap:'wrap', fontSize:10, color:'#7090a0' }}>
                        {(p.period_start || p.period_end) && <span>📅 {p.period_start} → {p.period_end}</span>}
                        {p.due_date && <span>Due: {p.due_date}</span>}
                        {p.paid_date && <span>Paid: {p.paid_date}</span>}
                        {p.payment_method && <span>💳 {p.payment_method.replace('_',' ')}</span>}
                        {p.notes && <span>📝 {p.notes}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── RECORD PAYMENT TAB ── */}
      {billingTab === 'record' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <Lbl>Invoice No</Lbl>
              <input className="inp" value={payForm.invoice_no || ''} onChange={e => setPayForm(f => ({ ...f, invoice_no: e.target.value }))} />
            </div>
            <div>
              <Lbl>Amount (MYR)</Lbl>
              <input className="inp" type="number" step="0.01" value={payForm.amount || ''} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
          </div>

          <div>
            <Lbl>Payment Status</Lbl>
            <div style={{ display:'flex', gap:8 }}>
              {[['paid','✓ Paid','#00e5a0'],['unpaid','⏳ Unpaid','#f5a623'],['overdue','⚠ Overdue','#e05757']].map(([val,label,c]) => (
                <button key={val} onClick={() => setPayForm(f => ({ ...f, status: val }))}
                  className="btn" style={{ flex:1, fontSize:11,
                    background: payForm.status === val ? c+'22' : 'none',
                    border: `1px solid ${payForm.status === val ? c : '#1a2a3a'}`,
                    color: payForm.status === val ? c : '#a0b4c0' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Lbl>Payment Method</Lbl>
            <select className="sel" style={{ width:'100%' }} value={payForm.payment_method || ''} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <Lbl>Due Date</Lbl>
              <input className="inp" type="date" value={payForm.due_date || ''} onChange={e => setPayForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            {payForm.status === 'paid' && (
              <div>
                <Lbl>Paid Date</Lbl>
                <input className="inp" type="date" value={payForm.paid_date || ''} onChange={e => setPayForm(f => ({ ...f, paid_date: e.target.value }))} />
              </div>
            )}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <Lbl>Period Start</Lbl>
              <input className="inp" type="date" value={payForm.period_start || ''} onChange={e => setPayForm(f => ({ ...f, period_start: e.target.value }))} />
            </div>
            <div>
              <Lbl>Period End</Lbl>
              <input className="inp" type="date" value={payForm.period_end || ''} onChange={e => setPayForm(f => ({ ...f, period_end: e.target.value }))} />
            </div>
          </div>

          <div>
            <Lbl>Notes (optional)</Lbl>
            <textarea className="inp" style={{ resize:'vertical', minHeight:50, fontSize:12 }}
              value={payForm.notes || ''} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Ref: TXN12345, paid via Maybank…" />
          </div>

          <div style={{ display:'flex', gap:10, marginTop:4 }}>
            <button className="btn" style={{ flex:1, background:'none', border:'1px solid #1a2a3a', color:'#a0b4c0' }}
              onClick={() => setBillingTab('payments')}>Cancel</button>
            <button className="btn" style={{ flex:2, background:ACCENT, color:'#060b10', fontWeight:700 }}
              onClick={savePayment} disabled={paySaving}>
              {paySaving ? 'Saving…' : '💾 Save Payment Record'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
